// src/components/ICT/ICTDashboard.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  Fade,
  Avatar,
  useTheme,
  alpha,
  CircularProgress,
  Skeleton,
  Paper,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Tooltip,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Assessment as AssessmentIcon,
  Computer as ComputerIcon,
  Security as SecurityIcon,
  Wifi as WifiIcon,
  Print as PrintIcon,
  Restaurant as RestaurantIcon,
  CheckCircle as CheckCircleIcon,
  WarningAmber as WarningAmberIcon,
  ErrorOutline as ErrorIcon,
  Info as InfoIcon,
 
  Storage as StorageIcon,
  People as PeopleIcon,
  Devices as DevicesIcon,
  Troubleshoot as TroubleshootIcon,
  MonitorHeart as MonitorHeartIcon,
  Settings as SettingsIcon,
  CloudUpload as CloudUploadIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';

// Import local components
import ICTHeader from '../../components/ICT/Header';
import ICTSidebar from '../../components/ICT/Sidebar';
import ICTFooter from '../../components/ICT/Footer';
import Preloader from '../../theme/Preloader';

// ============================================
// CONSTANTS & CONFIGURATION
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

// ============================================
// UTILITY FUNCTIONS
// ============================================

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const getInitials = (name) => {
  if (!name) return 'U';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

// ============================================
// STATISTICS CARD COMPONENT
// ============================================

const StatCard = React.memo(({ title, value, subtitle, icon, color, status, loading, onClick }) => {
  const theme = useTheme();
  
  if (loading) {
    return (
      <Card elevation={0} sx={{ borderRadius: '16px', height: '100%' }}>
        <CardContent>
          <Skeleton variant="rectangular" height={60} />
          <Skeleton variant="text" width="60%" />
        </CardContent>
      </Card>
    );
  }
  
  const statusColors = {
    healthy: { bg: '#E8F5E9', color: '#2E7D32', label: 'Healthy' },
    warning: { bg: '#FFF3E0', color: '#E65100', label: 'Warning' },
    error: { bg: '#FFEBEE', color: '#C62828', label: 'Error' },
    offline: { bg: '#F5F5F5', color: '#9E9E9E', label: 'Offline' },
  };
  
  const statusInfo = status ? statusColors[status] || statusColors.healthy : null;
  
  return (
    <Card 
      elevation={0} 
      sx={{ 
        borderRadius: '16px', 
        height: '100%',
        border: `1px solid ${alpha(color || '#1976D2', 0.12)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        },
        position: 'relative',
        overflow: 'hidden',
      }}
      onClick={onClick}
    >
      <Box 
        sx={{ 
          position: 'absolute', 
          top: 0, 
          right: 0, 
          width: '100px', 
          height: '100px',
          borderRadius: '0 16px 0 100px',
          bgcolor: alpha(color || '#1976D2', 0.06),
          pointerEvents: 'none',
        }} 
      />
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="caption" 
              color="text.secondary" 
              fontWeight={700}
              sx={{ 
                fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                textTransform: 'uppercase', 
                letterSpacing: '0.5px',
                display: 'block',
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h5" 
              fontWeight={800}
              sx={{ 
                fontSize: { xs: '1.0rem', sm: '1.2rem', md: '1.4rem' },
                mt: 0.5,
                color: color || 'text.primary',
                wordBreak: 'break-word',
              }}
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.8rem' }, mt: 0.25, display: 'block' }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <Avatar 
              sx={{ 
                bgcolor: alpha(color || '#1976D2', 0.12),
                color: color || '#1976D2',
                width: { xs: 44, sm: 52, md: 56 },
                height: { xs: 44, sm: 52, md: 56 },
                flexShrink: 0,
                ml: 1,
              }}
            >
              {icon}
            </Avatar>
            {statusInfo && (
              <Chip
                size="small"
                label={statusInfo.label}
                sx={{
                  fontSize: '0.5rem',
                  height: 18,
                  fontWeight: 700,
                  bgcolor: statusInfo.bg,
                  color: statusInfo.color,
                  mt: 0.5,
                }}
              />
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
});

StatCard.displayName = 'StatCard';

// ============================================
// MAIN ICT DASHBOARD COMPONENT
// ============================================

const ICTDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // ============================================
  // STATE MANAGEMENT
  // ============================================
  
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // ============================================
  // REFS
  // ============================================
  
  const isMountedRef = useRef(true);
  const refreshIntervalRef = useRef(null);

  // ============================================
  // DUMMY DATA
  // ============================================
  
  const getDummyData = useCallback(() => {
    return {
      system_status: {
        overall: 'healthy',
        uptime: '99.8%',
        last_check: new Date().toISOString(),
      },
      devices: {
        total: 12,
        online: 10,
        offline: 2,
        issues: 1,
      },
      network: {
        status: 'healthy',
        speed: '100 Mbps',
        latency: '12ms',
        packet_loss: '0.1%',
      },
      printers: {
        total: 8,
        online: 7,
        offline: 1,
        paper_low: 2,
      },
      pos_stations: {
        total: 6,
        active: 4,
        maintenance: 1,
        offline: 1,
      },
      security: {
        issues: 0,
        last_scan: new Date(Date.now() - 3600000).toISOString(),
        firewall: 'Active',
      },
      recent_alerts: [
        { id: 1, type: 'warning', message: 'Printer #3 is low on paper', time: new Date(Date.now() - 1800000).toISOString(), location: 'Continental' },
        { id: 2, type: 'info', message: 'System backup completed successfully', time: new Date(Date.now() - 3600000).toISOString(), location: 'Server' },
        { id: 3, type: 'error', message: 'POS Station #2 offline', time: new Date(Date.now() - 7200000).toISOString(), location: 'Lounge' },
        { id: 4, type: 'warning', message: 'Storage reaching 85% capacity', time: new Date(Date.now() - 14400000).toISOString(), location: 'Cloud' },
        { id: 5, type: 'info', message: 'Software update available for v3.2.1', time: new Date(Date.now() - 86400000).toISOString(), location: 'System' },
      ],
      quick_actions: [
        { id: 1, label: 'System Health Check', icon: <MonitorHeartIcon />, color: '#1976D2' },
        { id: 2, label: 'Network Diagnostic', icon: <WifiIcon />, color: '#2E7D32' },
        { id: 3, label: 'Printer Management', icon: <PrintIcon />, color: '#FF9800' },
        { id: 4, label: 'Security Audit', icon: <SecurityIcon />, color: '#9C27B0' },
      ],
      location_status: [
        { location: 'Continental', devices: 4, online: 3, issues: 0, status: 'healthy' },
        { location: 'Lounge', devices: 3, online: 2, issues: 1, status: 'warning' },
        { location: 'African', devices: 3, online: 3, issues: 0, status: 'healthy' },
        { location: 'Back Office', devices: 2, online: 2, issues: 0, status: 'healthy' },
      ],
    };
  }, []);

  // ============================================
  // DATA FETCHING
  // ============================================
  
  const fetchDashboardData = useCallback(async () => {
    try {
      console.debug('[ICT] Fetching dashboard data...');
      setIsFetching(true);
      
      // Simulate API call with dummy data
      await new Promise(resolve => setTimeout(resolve, 500));
      const data = getDummyData();
      
      console.debug('[ICT] Dashboard data fetched:', data);
      
      if (isMountedRef.current) {
        setDashboardData(data);
      }
      
      return data;
    } catch (err) {
      console.error('[ICT] Error fetching dashboard data:', err);
      if (isMountedRef.current) {
        setSnackbar({
          open: true,
          message: `Failed to fetch dashboard: ${err.message}`,
          severity: 'error',
        });
      }
      return null;
    } finally {
      if (isMountedRef.current) {
        setIsFetching(false);
      }
    }
  }, [getDummyData]);

  const loadData = useCallback(async () => {
    await fetchDashboardData();
  }, [fetchDashboardData]);

  // ============================================
  // EFFECTS
  // ============================================
  
  // Initial load
  useEffect(() => {
    isMountedRef.current = true;
    
    console.debug('[ICT] Initializing ICT Dashboard...');
    
    loadData();
    
    // Auto-refresh every 30 seconds
    refreshIntervalRef.current = setInterval(() => {
      if (isMountedRef.current && !isFetching) {
        console.debug('[ICT] Auto-refreshing...');
        loadData();
      }
    }, 30000);
    
    const timer = setTimeout(() => {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }, 600);
    
    return () => {
      clearTimeout(timer);
      clearInterval(refreshIntervalRef.current);
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // HANDLERS
  // ============================================
  
  const handleRefresh = useCallback(async () => {
    console.debug('[ICT] Manual refresh triggered');
    await loadData();
    setSnackbar({
      open: true,
      message: 'Data refreshed successfully!',
      severity: 'success',
    });
  }, [loadData]);

  const handleQuickAction = useCallback((action) => {
    setSnackbar({
      open: true,
      message: `Executing: ${action.label}...`,
      severity: 'info',
    });
    // Simulate action execution
    setTimeout(() => {
      setSnackbar({
        open: true,
        message: `${action.label} completed successfully!`,
        severity: 'success',
      });
    }, 1500);
  }, []);

  // ============================================
  // RENDER FUNCTIONS
  // ============================================
  
  const renderSystemStatus = useCallback(() => {
    if (!dashboardData) return null;
    
    const { system_status } = dashboardData;
    const statusColors = {
      healthy: { bg: '#E8F5E9', color: '#2E7D32', icon: <CheckCircleIcon /> },
      warning: { bg: '#FFF3E0', color: '#E65100', icon: <WarningAmberIcon /> },
      error: { bg: '#FFEBEE', color: '#C62828', icon: <ErrorIcon /> },
    };
    
    const statusInfo = statusColors[system_status.overall] || statusColors.healthy;
    
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: '16px',
          mb: 3,
          border: `2px solid ${statusInfo.color}`,
          bgcolor: alpha(statusInfo.color, 0.04),
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                bgcolor: alpha(statusInfo.color, 0.12),
                color: statusInfo.color,
                width: { xs: 48, sm: 56 },
                height: { xs: 48, sm: 56 },
              }}
            >
              {statusInfo.icon}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
                System Status: {system_status.overall.toUpperCase()}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, fontWeight: 600 }}>
                Uptime: {system_status.uptime} • Last check: {formatDateTime(system_status.last_check)}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={`Uptime: ${system_status.uptime}`}
            sx={{
              fontWeight: 700,
              bgcolor: alpha(statusInfo.color, 0.1),
              color: statusInfo.color,
              fontSize: { xs: '0.7rem', sm: '0.8rem' },
            }}
          />
        </Box>
      </Paper>
    );
  }, [dashboardData]);

  const renderSummaryCards = useCallback(() => {
    if (!dashboardData) return null;
    
    const { devices, network, printers, pos_stations, security } = dashboardData;
    
    const cards = [
      {
        title: 'Devices',
        value: `${devices.online}/${devices.total}`,
        subtitle: `${devices.offline} offline • ${devices.issues} issues`,
        icon: <DevicesIcon />,
        color: devices.offline > 0 ? '#FF9800' : '#2E7D32',
        status: devices.offline > 0 ? 'warning' : 'healthy',
      },
      {
        title: 'Network',
        value: network.speed,
        subtitle: `Latency: ${network.latency} • Loss: ${network.packet_loss}`,
        icon: <WifiIcon />,
        color: network.status === 'healthy' ? '#2E7D32' : '#FF9800',
        status: network.status,
      },
      {
        title: 'Printers',
        value: `${printers.online}/${printers.total}`,
        subtitle: `${printers.offline} offline • ${printers.paper_low} low paper`,
        icon: <PrintIcon />,
        color: printers.offline > 0 || printers.paper_low > 0 ? '#FF9800' : '#2E7D32',
        status: printers.offline > 0 ? 'error' : printers.paper_low > 0 ? 'warning' : 'healthy',
      },
      {
        title: 'POS Stations',
        value: `${pos_stations.active}/${pos_stations.total}`,
        subtitle: `${pos_stations.maintenance} maintenance • ${pos_stations.offline} offline`,
        icon: <ComputerIcon />,
        color: pos_stations.offline > 0 ? '#F44336' : pos_stations.maintenance > 0 ? '#FF9800' : '#2E7D32',
        status: pos_stations.offline > 0 ? 'error' : pos_stations.maintenance > 0 ? 'warning' : 'healthy',
      },
      {
        title: 'Security',
        value: security.issues === 0 ? '✅ Secure' : `${security.issues} issues`,
        subtitle: `Last scan: ${formatDateTime(security.last_scan)} • Firewall: ${security.firewall}`,
        icon: <SecurityIcon />,
        color: security.issues === 0 ? '#2E7D32' : '#F44336',
        status: security.issues === 0 ? 'healthy' : 'error',
      },
    ];
    
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={6} md={2.4} key={index}>
            <StatCard {...card} loading={isFetching} />
          </Grid>
        ))}
      </Grid>
    );
  }, [dashboardData, isFetching]);

  const renderAlerts = useCallback(() => {
    if (!dashboardData || !dashboardData.recent_alerts || dashboardData.recent_alerts.length === 0) {
      return null;
    }
    
    const severityMap = {
      error: { severity: 'error', icon: <ErrorIcon /> },
      warning: { severity: 'warning', icon: <WarningAmberIcon /> },
      info: { severity: 'info', icon: <InfoIcon /> },
    };
    
    const alerts = dashboardData.recent_alerts.slice(0, 3);
    
    return (
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px', mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
            ⚠️ Recent Alerts
          </Typography>
          <Chip
            size="small"
            label={`${dashboardData.recent_alerts.length} alerts`}
            color="warning"
            sx={{ fontWeight: 700 }}
          />
        </Box>
        <Stack spacing={1.5}>
          {alerts.map((alert) => {
            const { severity, icon } = severityMap[alert.type] || severityMap.info;
            return (
              <Alert
                key={alert.id}
                severity={severity}
                icon={icon}
                variant="outlined"
                sx={{
                  borderRadius: '10px',
                  '& .MuiAlert-message': {
                    width: '100%',
                  },
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: 1,
                  width: '100%',
                }}>
                  <Typography variant="body2" fontWeight={700} sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                    {alert.message}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                    <Chip
                      size="small"
                      label={alert.location}
                      sx={{ fontSize: '0.6rem', fontWeight: 700, height: 20 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                      {formatDateTime(alert.time)}
                    </Typography>
                  </Box>
                </Box>
              </Alert>
            );
          })}
        </Stack>
      </Paper>
    );
  }, [dashboardData]);

  const renderQuickActions = useCallback(() => {
    if (!dashboardData) return null;
    
    return (
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px', mb: 3 }}>
        <Typography variant="h6" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
          ⚡ Quick Actions
        </Typography>
        <Grid container spacing={2}>
          {dashboardData.quick_actions.map((action) => (
            <Grid item xs={6} sm={3} key={action.id}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: '12px',
                  border: `1px solid ${alpha(action.color, 0.15)}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[4],
                    borderColor: action.color,
                  },
                }}
                onClick={() => handleQuickAction(action)}
              >
                <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(action.color, 0.1),
                      color: action.color,
                      width: { xs: 44, sm: 52 },
                      height: { xs: 44, sm: 52 },
                      mx: 'auto',
                      mb: 1,
                    }}
                  >
                    {action.icon}
                  </Avatar>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{
                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      color: action.color,
                    }}
                  >
                    {action.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  }, [dashboardData, theme, handleQuickAction]);

  const renderLocationStatus = useCallback(() => {
    if (!dashboardData) return null;
    
    return (
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px' }}>
        <Typography variant="h6" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
          📍 Location Status
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha('#000', 0.02) }}>
                <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' }, fontWeight: 800 }}>Location</TableCell>
                <TableCell align="center" sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' }, fontWeight: 800 }}>Devices</TableCell>
                <TableCell align="center" sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' }, fontWeight: 800 }}>Online</TableCell>
                <TableCell align="center" sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' }, fontWeight: 800 }}>Issues</TableCell>
                <TableCell align="center" sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' }, fontWeight: 800 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dashboardData.location_status.map((loc) => {
                const statusColors = {
                  healthy: { bg: '#E8F5E9', color: '#2E7D32', label: 'Healthy' },
                  warning: { bg: '#FFF3E0', color: '#E65100', label: 'Warning' },
                  error: { bg: '#FFEBEE', color: '#C62828', label: 'Error' },
                };
                const statusInfo = statusColors[loc.status] || statusColors.healthy;
                
                return (
                  <TableRow key={loc.location} hover>
                    <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' }, fontWeight: 700 }}>
                      {loc.location}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>
                      {loc.devices}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>
                      {loc.online}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>
                      {loc.issues}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={statusInfo.label}
                        sx={{
                          fontSize: { xs: '0.6rem', sm: '0.7rem' },
                          fontWeight: 700,
                          bgcolor: statusInfo.bg,
                          color: statusInfo.color,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }, [dashboardData]);

  // ============================================
  // LOADING STATE
  // ============================================
  
  if (isLoading) {
    return <Preloader text="Loading ICT Dashboard..." size={70} fullScreen />;
  }

  // ============================================
  // MAIN RENDER - Using epos-wrapper pattern
  // ============================================

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <ICTSidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />
        
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
        }}>
          <ICTHeader />
          
          <Box sx={{
            flex: 1,
            overflow: 'auto',
            p: { xs: 1.5, sm: 2, md: 3 },
            bgcolor: '#f8fafc',
            '&::-webkit-scrollbar': {
              width: 6,
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'grey.300',
              borderRadius: 3,
            },
          }}>
            {/* Header Section */}
            <Box sx={{
              bgcolor: 'white',
              borderRadius: '16px',
              p: { xs: 1.5, sm: 2.5, md: 3 },
              mb: { xs: 1.5, sm: 2.5, md: 3 },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexWrap: 'wrap',
              gap: 2,
              border: `1px solid ${alpha('#1976D2', 0.12)}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}>
              <Box>
                <Typography variant="h4" sx={{
                  color: '#1976D2',
                  fontWeight: 800,
                  fontSize: { xs: '1.4rem', sm: '2rem', md: '2.4rem' },
                }}>
                  🖥️ ICT Dashboard
                  <span style={{
                    display: 'inline-block',
                    width: '4px',
                    height: { xs: '28px', sm: '36px', md: '40px' },
                    backgroundColor: '#1976D2',
                    marginLeft: '8px',
                    verticalAlign: 'text-bottom',
                    animation: 'blink 1s step-end infinite',
                  }} />
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' }, fontWeight: 600 }}>
                  System Monitoring & Management • {formatDate(new Date())}
                </Typography>
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                gap: { xs: 1, sm: 1.5 }, 
                flexWrap: 'wrap',
                width: { xs: '100%', sm: 'auto' },
              }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleRefresh}
                  startIcon={isFetching ? <CircularProgress size={22} color="inherit" /> : <RefreshIcon />}
                  sx={{
                    bgcolor: '#1976D2',
                    '&:hover': { bgcolor: '#1565C0' },
                    textTransform: 'none',
                    fontWeight: 800,
                    fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.85rem' },
                    borderRadius: '10px',
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.5, sm: 0.75 },
                    flex: { xs: 1, sm: 'none' },
                  }}
                  disabled={isFetching}
                >
                  {isFetching ? 'Loading...' : 'Refresh'}
                </Button>
              </Box>
            </Box>

            {/* System Status */}
            {renderSystemStatus()}

            {/* Summary Cards */}
            {renderSummaryCards()}

            {/* Alerts */}
            {renderAlerts()}

            {/* Quick Actions */}
            {renderQuickActions()}

            {/* Location Status */}
            {renderLocationStatus()}

            {/* Footer Info */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                Last updated: {formatDateTime(new Date())} • Auto-refresh every 30 seconds
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <ICTFooter />
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '10px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      <style>
        {`
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }
        `}
      </style>
    </Box>
  );
};

export default ICTDashboard;